/**
 * Full-page view for essay supporting info (attachments, linked notes, organization).
 *
 * Called by:
 * - App.tsx as route for /reviewer/essays/:slug/info
 */
import { useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Typography, Button, Space, Spin, Tabs, Divider, Badge } from 'antd';
import {
  ArrowLeftOutlined,
  PaperClipOutlined,
  FileSearchOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getEssay, getAttachments, getLinkedNotes, getEssayTags, getEssaySeriesInfo } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import AttachmentsPanel from '../components/AttachmentsPanel';
import LinkedNotesPanel from '../components/LinkedNotesPanel';
import CategoryTagSelector from '../components/CategoryTagSelector';
import SeriesManager from '../components/SeriesManager';
import type { Category, LinkedNoteFromEssay } from '@compendium/shared';

const { Title, Text } = Typography;

export default function EssayInfoPage() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: essay, isLoading: essayLoading } = useQuery({
    queryKey: ['essay', slug],
    queryFn: () => getEssay(slug!),
    enabled: !!slug,
  });

  const { data: attachments = [], isLoading: attachmentsLoading } = useQuery({
    queryKey: ['attachments', slug],
    queryFn: () => getAttachments(slug!),
    enabled: !!slug && !!user,
  });

  const { data: linkedNotes = [], isLoading: notesLoading } = useQuery<LinkedNoteFromEssay[]>({
    queryKey: ['linkedNotes', slug],
    queryFn: () => getLinkedNotes(slug!),
    enabled: !!slug && !!user,
  });

  const { data: essayTags = [] } = useQuery({
    queryKey: ['essayTags', slug],
    queryFn: () => getEssayTags(slug!),
    enabled: !!slug && !!user,
  });

  const { data: seriesInfo = [] } = useQuery({
    queryKey: ['essaySeries', slug],
    queryFn: () => getEssaySeriesInfo(slug!),
    enabled: !!slug && !!user,
  });

  const handleAttachmentsChange = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['attachments', slug] });
  }, [queryClient, slug]);

  const handleNotesChange = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['linkedNotes', slug] });
  }, [queryClient, slug]);

  const handleCategoryChange = useCallback((category: Category | null) => {
    queryClient.setQueryData(['essay', slug], (old: any) => {
      if (!old) return old;
      return { ...old, categoryId: category?.id || null };
    });
  }, [queryClient, slug]);

  const handleTagsChange = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['essayTags', slug] });
  }, [queryClient, slug]);

  const handleSeriesChange = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['essaySeries', slug] });
  }, [queryClient, slug]);

  if (essayLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Link to={`/reviewer/essays/${slug}`}>
          <Button type="text" icon={<ArrowLeftOutlined />} style={{ paddingLeft: 0, marginBottom: 8 }}>
            Back to Essay
          </Button>
        </Link>
        <Space>
          <PaperClipOutlined style={{ fontSize: 20, color: '#d4af37' }} />
          <Title level={3} style={{ margin: 0 }}>Supporting Info</Title>
        </Space>
        {essay && (
          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
            {essay.title}
          </Text>
        )}
      </div>

      {/* Tabbed content */}
      {user && (
        <Tabs
          defaultActiveKey="attachments"
          items={[
            {
              key: 'attachments',
              label: (
                <Space>
                  <PaperClipOutlined />
                  Attachments
                  {attachments.length > 0 && (
                    <Badge count={attachments.length} size="small" />
                  )}
                </Space>
              ),
              children: (
                <AttachmentsPanel
                  slug={slug!}
                  attachments={attachments}
                  isLoading={attachmentsLoading}
                  user={user}
                  onAttachmentsChange={handleAttachmentsChange}
                />
              ),
            },
            {
              key: 'notes',
              label: (
                <Space>
                  <FileSearchOutlined />
                  Notes
                  {linkedNotes.length > 0 && (
                    <Badge count={linkedNotes.length} size="small" />
                  )}
                </Space>
              ),
              children: (
                <LinkedNotesPanel
                  slug={slug!}
                  linkedNotes={linkedNotes}
                  isLoading={notesLoading}
                  user={user}
                  onNotesChange={handleNotesChange}
                />
              ),
            },
            {
              key: 'organization',
              label: (
                <Space>
                  <FolderOutlined />
                  Organization
                </Space>
              ),
              children: (
                <div>
                  <CategoryTagSelector
                    slug={slug!}
                    currentCategoryId={essay?.categoryId ?? null}
                    currentTagIds={essayTags.map((t) => t.id)}
                    user={user}
                    onCategoryChange={handleCategoryChange}
                    onTagsChange={() => handleTagsChange()}
                  />
                  <Divider />
                  <SeriesManager
                    slug={slug!}
                    seriesInfo={seriesInfo}
                    user={user}
                    onSeriesChange={handleSeriesChange}
                  />
                </div>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}
